import React, { useState, useRef, useEffect } from 'react' // 27 Aug 2025 - added useRef
import useTitle from "@/hooks/useTitle"
import {
    PlusIcon,
    MinusIcon,
    PencilSquareIcon,
  } from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLight2 from "@/utils/EtContentEditorLight2"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import { useNavigate } from "react-router"

import { useUpdateUserPlanPartnerMutation, useGetUserPlanPartnersQuery } from '@/features/userPlans/userPlanPartnersApiSlice'
import { useGetUserMissionPartnersQuery } from '@/features/userMissions/userMissionPartnersApiSlice'
import { useGetActionTypesQuery } from "@/features/actionTypes/actionTypesApiSlice"
import { useGetTrailsQuery } from "@/features/trails/trailsApiSlice"
import { useGetSessionsQuery } from "@/features/sessions/sessionsApiSlice"

// 27 Aug 2025
import { useGetUserMissionGoalsQuery } from "@/features/userMissionGoals/userMissionGoalsApiSlice"

import { produce } from "immer";

const EtUpdateUserImprintPlanPartnerComp = ({
    id,
    userId, 
    userOrgId, 
    triggeringUrl, 
    onClose
  }) => {

  // First Created - 16 Jan 2025
  // Author - Atul Pandey
  // Inherited from the EtCreateUserImprintGratitudeComp component
  // Updated - 25 Aug 2025 - updated sessionId - to a select list instead of hardcoded value
  // Updated - 27 Aug 2025 - added goals

  // 0 - Set Page title & variables
  useTitle('User Imprint Partner Plan - Update')

  // console.log('id is:', id)
  // console.log('userId is:', userId)
  // console.log('userOrgId is:', userOrgId)
  
  // 1a - set page navigations
  const navigate = useNavigate()
  const originalPlanGoalsRef = useRef([]); // 27 Aug 2025
  

  // 1b - set form states
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  // 27 Aug 2025 - Plan Goals update support
  const [planGoalsMarkedForDeletion, setPlanGoalsMarkedForDeletion] = useState([]);
  const [selectedGoalId, setSelectedGoalId] = useState(""); // for dropdown UI
  
  // 2a - set the mutation to create the new user imprint gratitude data
    const [updateUserPlanPartner, {
      isLoading,
      isSuccess,
      isError,
      error,
    }] = useUpdateUserPlanPartnerMutation()
  
  // 2b - Get existing user creations Data from the backend
  const { 
    data: userPlanPartnerData,
    isLoading: isFetching,
    isError: isFetchError,
    error: fetchError,
  } = useGetUserPlanPartnersQuery()
  // console.log('userPlanPartnerData', userPlanPartnerData)
  const userPlanPartnerRecords = userPlanPartnerData?.ids
  ?.map(id => userPlanPartnerData?.entities[id])
  ?.filter(item=> item._id === id && item.userId._id === userId && item.userOrgId._id === userOrgId) || []
  console.log('userPlanPartners', userPlanPartnerRecords)

  // 2c - get the data for plan value uom and gradations
    const { data: missionsData } = useGetUserMissionPartnersQuery()
    const { data: trailsData } = useGetTrailsQuery()
    const { data: sessionsData } = useGetSessionsQuery()
    const { data: actionTypesData } = useGetActionTypesQuery()   

    //27 Aug 2025
    const { data: userMissionGoalsData } = useGetUserMissionGoalsQuery()
  
    const extractEntitiesFromAdapter = (dataAdapter) => {
      if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
      return dataAdapter.ids.map((id) => dataAdapter.entities[id])
    };
  
    const extractFilteredEntitiesFromAdapter = (dataAdapter) => {
      if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
      return dataAdapter.ids.map((id) => dataAdapter.entities[id])
      ?.filter(
          (item) => item.userId?._id === userId && item.userOrgId._id === userOrgId
          );
    };
  
    const missions = extractFilteredEntitiesFromAdapter(missionsData)
    const trails = extractEntitiesFromAdapter(trailsData)
    const sessions = extractEntitiesFromAdapter(sessionsData)
    const actionTypes = extractEntitiesFromAdapter(actionTypesData)

    // 27 Aug 2025
    const userMissionGoals = extractEntitiesFromAdapter(userMissionGoalsData)

    // Filter: Only those tied to missionModel = 'UserMissionPartner'
    const userMissionPartnerGoals = userMissionGoals?.filter(goal =>
      goal.missionModel === 'UserMissionPartner'
    );

  // console.log('userMission Partner Goals', userMissionPartnerGoals)
    
    const trailId = trails?.find(item => item.trailName === 'MyMissions')?._id;
    // const sessionId = sessions?.find(item => item.sessionName === 'Plan a Mission')?._id; // 25 Aug 2025
    const actionTypeId = actionTypes?.find(item => item.actionTypeName === 'Mission')?._id;
    console.log('missions', missions)
    // console.log('uoms', metricUoMs)
    // console.log('gradations', metricGradations)
  

  // 3 - Initialize Form Data State
  const [formData, setFormData] = useState({
      missionId: null,
      sessionId: "", // ✅ 25 Aug 2025 - new field added
      planName: "",
      planDesc: "",
      planDate: "",
      newActions: [], // Reset newActions
      planActions: [], // Reset PlanActions
      existingActions: [], // Reset Existing Actions
      planNotes: [],
      planGoals:[],     // 27 Aug 2025
      pic: "",
      active: true, // Default active state
  });

  // 4a - 27 Aug 2025 - Resolve goals that belong to the selected missionId
  const filteredResolvedGoals = userMissionPartnerGoals
  ?.filter((goal) => goal.missionId?._id === formData.missionId) // 💡 Match current selected mission
  ?.map((goal) => ({
    ...goal,
    displayName: goal.sharedGoalId ? goal?.sharedGoalId?.goalName : goal?.goalName || "",
    missionGoalId: goal?._id,
    sharedMissionGoalId: goal?.sharedGoalId?._id || null,
  }));

  const availableGoalsForDropdown = filteredResolvedGoals?.filter((goal) => {
    return !formData.planGoals?.some(
      (selected) => selected.connectedMissionGoalId === goal.missionGoalId
    );
  });
  
  // console.log('userMission filtered goals', filteredResolvedGoals)
  // console.log('formData missionId', formData.missionId)

  // 4b - Set the FormData based on the incoming data
  useEffect(() => {
    if (userPlanPartnerRecords?.length > 0 && !isFormInitialized) {
      const selectedRecord = userPlanPartnerRecords[0];

    // 🔁 27 Aug 2025 - Deep clone to isolate original plan goals
    const originalPlanGoals = JSON.parse(JSON.stringify(selectedRecord?.planGoals || []));
    originalPlanGoalsRef.current = originalPlanGoals;
  
      setFormData({
        missionId: selectedRecord?.missionId._id || null,
        sessionId: selectedRecord?.sessionId?._id || "", // 25 Aug 2025 ✅ new line
        planName: selectedRecord?.planName || "",
        planDesc: selectedRecord?.planDesc || "",
        planDate: selectedRecord?.planDate || "",
        planActions: selectedRecord?.planActions || [],
        existingActions: selectedRecord?.planActions?.map((action) => ({
          actionId: action.planActionId?._id || null,
          userActionName: action.planActionId?.userActionName || "",
          userActionDesc: action.planActionId?.userActionDesc || "",
          completeBy: action.planActionId?.completeBy || "",
        })) || [],
        planNotes: selectedRecord?.planNotes || [],
        planGoals: originalPlanGoals, // ✅ 27 Aug 2025 - Inject editable planGoals
        aboutMe: selectedRecord?.aboutMe || '',
        pic: selectedRecord?.pic || '',
        active: selectedRecord?.active ?? true,
      });
  
      setIsFormInitialized(true); // Prevent future overwrites
    }
  }, [userPlanPartnerRecords, isFormInitialized]);
  
 
  // 5a - Convert Image to Base64
  const convertPicToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  // 5b - Handle Form Changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "pic") {
      setFormData((prevState) => ({ ...prevState, pic: files[0] }));
    } else {
      setFormData((prevState) => ({ ...prevState, [name]: value }));
    }
  };
  
  // 5c - Toggle show / collapse each section 
  const toggleSection = (key) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // 5d - Function to add a new action to `newActions`
  const handleAddAction = () => {
    setFormData((prevState) =>
      produce(prevState, (draft) => {
        if (!draft.newActions) {
          draft.newActions = []; // Ensure newActions is always an array
        }
        draft.newActions.push({
          userActionName: "",
          userActionDesc: "",
          completeBy: "",
          actionTargetStakeholders: [{ stakeholderTypeName: "Partner" }],
        });
      })
    );
  };
  
  
  // 5e - Function to remove a new action to `newActions`
  const handleRemoveAction = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      newActions: prevState.newActions.filter((_, idx) => idx !== index),
    }));
    setCollapsedSections((prev) => {
      const updatedSections = { ...prev };
      delete updatedSections[`action-${index}`]; // Remove the collapsed section state for the action
      return updatedSections;
    });
  };

  // 5f - Function to handle action change
  const handleActionChange = (index, fieldName, value) => {
    setFormData((prevState) => ({
      ...prevState,
      newActions: prevState.newActions.map((action, idx) =>
        idx === index ? { ...action, [fieldName]: value } : action
      ),
    }));
  };

  // 5g - Function to add Plan Goal (with Immer) - 27 Aug 2025
  const handleAddGoalToPlan = () => {
    if (!selectedGoalId) return;

    const selectedGoal = filteredResolvedGoals.find(goal => goal.missionGoalId === selectedGoalId);
    if (!selectedGoal) return;

    setFormData(produce(draft => {
      draft.planGoals = draft.planGoals || [];

      // Avoid duplicate add
      const alreadyAdded = draft.planGoals.some(goal => goal.connectedMissionGoalId === selectedGoalId);
      if (alreadyAdded) return;

      draft.planGoals.push({
        connectedMissionGoalId: selectedGoal.missionGoalId,
        connectedSharedGoalId: selectedGoal.sharedMissionGoalId || null,
      });
    }));

    setSelectedGoalId("");
  };

  // 5h - Remove Plan Goal (with Immer + mark for deletion if it has _id) - 27 Aug 2025
  const handleRemoveGoalFromPlan = (goal, index) => {
    setFormData(produce(draft => {
      const removed = draft.planGoals.splice(index, 1)[0]; // remove from array

      if (removed?._id) {
        setPlanGoalsMarkedForDeletion(prev => [
          ...prev,
          { ...removed, markDeleted: true },
        ]);
      }
    }));
  };

  // 6 - Handle form subplan
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Convert image to Base64 if it exists
      const picBase64 = formData.pic ? await convertPicToBase64(formData.pic) : undefined;

      // Validate and filter notes
      const validNotes = formData.planNotes?.filter((note) => !note.delete && note.planNoteText.trim());
      const deletedNotes = formData.planNotes?.filter((note) => note.delete);

      // Validate required fields
      if (!formData.planName.trim()) {
        alert("Plan name is required.");
        return;
      }

      if (!formData.missionId) {
        alert("A mission must be selected.");
        return;
      }

      // 27 Aug 2025
      // 🧠 Step 1 - Validate and prepare planGoals
      const updatedPlanGoalsPayload = [];

      formData.planGoals?.forEach((goal) => {
        const trimmedGoal = {
          ...goal,
          connectedMissionGoalId: goal.connectedMissionGoalId || null,
          connectedSharedGoalId: goal.sharedMissionGoalId || null,
          connectedGoalBreakdownId: goal.connectedGoalBreakdownId || null,
        };

        if (goal._id) {
          // Existing goal being updated
          updatedPlanGoalsPayload.push({
            _id: goal._id,
            ...trimmedGoal,
          });
        } else {
          // New goal
          updatedPlanGoalsPayload.push(trimmedGoal);
        }
      });

      // 🧹 Step 2 - Append marked-for-deletion goals
      planGoalsMarkedForDeletion?.forEach((goal) => {
        if (goal._id) {
          updatedPlanGoalsPayload.push({
            _id: goal._id,
            markDeleted: true,
            connectedMissionGoalId: goal.connectedMissionGoalId || null,
            connectedSharedGoalId: goal.sharedMissionGoalId || null,
          });
        }
      });

      // Construct the payload
      const payload = {
        planPartnerId: id, // Identifier for the record to update
        userId,
        userOrgId,
        missionId: formData.missionId,
        trailId: trailId,
        // sessionId: sessionId,
        sessionId: formData.sessionId,    // 25 Aug 2025
        planName: formData.planName.trim(),
        planDesc: formData.planDesc?.trim() || undefined,
        planDate: formData.planDate || undefined,
        pic: picBase64 || undefined,
        active: formData.active,
        newActions: formData.newActions?.map((action) => ({
          ...action,
          userActionName: action.userActionName.trim(),
          completeBy: action.completeBy,
          actionTypeId, // Use the pre-determined action type ID
        })),
        existingActions: formData.existingActions?.map((action) => ({
          actionId: action.actionId,
          userActionName: action.userActionName?.trim(),
          userActionDesc: action.userActionDesc?.trim(),
          completeBy: action.completeBy,
        })),
        planNotes: [
          ...validNotes.map((note) => ({
            _id: note._id || undefined, // For existing notes
            planNoteText: note.planNoteText.trim(),
            planNoteDate: note.planNoteDate,
          })),
          ...deletedNotes.map((note) => ({
            _id: note._id, // For deleted notes, _id is required
            delete: true,
          })),
        ],
        planGoals: updatedPlanGoalsPayload, // 27 Aug 2025
      };

      // console.log("Payload:", payload); // Debugging purposes

      // Update the planPartner using the mutation
      await updateUserPlanPartner(payload).unwrap();

      // Handle success
      if (onClose) {
        onClose(); // Close modal if onClose is provided
      } else {
        navigate(triggeringUrl || "/etapp"); // Redirect on success
      }
    } catch (error) {
      console.error("Error updating planPartner:", error);
      alert("Failed to update the plan. Please try again.");
    }
  };

  // 7 - Reset Form on Successful Subplan
  useEffect(() => {
    if (isSuccess) {
      setFormData({
        missionId: null,
        planName: "",
        planDesc: "",
        planDate: "",
        newActions: [], // Reset newActions
        existingActions: [], // Reset Existing Actions
        planNotes: [],
        planGoals:[],     // 27 Aug 2025
        pic: "",
        active: true, // Default active state
      });

      if (onClose) {
        onClose();
      } else {
        navigate(triggeringUrl || "/etapp");
      }
    }
  }, [isSuccess, navigate]);
  
  // 8 - Handle Clear
  const handleClear = () => {
    setFormData({
      missionId: null,
      planName: "",
      planDesc: "",
      planDate: "",
      newActions: [], // Reset newActions
      existingActions: [], // Reset Existing Actions
      planNotes: [],
      planGoals:[],     // 27 Aug 2025
      pic: "",
      active: true, // Default active state
    });
  };

  // 9 - Handle Cancel
  const handleCancel = () => {
    if (onClose) {
        onClose();
    } else {
        navigate(triggeringUrl || "/etapp");
    }
  };

  // console.log('FormData:', formData)

  return (
    <div className="mx-auto w-full m-1 p-1 bg-white rounded shadow-md shadow-black">
      {isLoading && <PulseLoader color={'#ea580c'} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">{error?.data?.message}</p>
      )}

      <p className="p-1 m-1 font-bold font-poppins text-xl">
        Update User Partner Plan
      </p>

      <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
        
        {/* 1. Partner Plan - Name */}
        <div id="planPartnerName" className="bg-gray-100 rounded p-1 m-1">
          <label htmlFor="planName" className="block text-gray-600 text-sm font-bold mb-1">
            Partner Plan Name
          </label>
          <input
            type="text"
            name="planName"
            value={formData.planName}
            onChange={(e) =>
              setFormData((prev) =>
                produce(prev, (draft) => {
                  draft.planName = e.target.value;
                })
              )
            }
            placeholder="Enter planPartner name"
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        {/* 2. Partner Plan - Description */}
        <div id="planPartnerDesc" className="bg-gray-100 rounded p-1 m-1">
          <label
            htmlFor="planDesc"
            className="block text-gray-600 text-sm font-bold mb-1"
          >
            Partner Plan Description
          </label>
          {isFormInitialized && ( 
          <EtContentEditorLight2
            value={formData.planDesc}
            handleChange={(content) => {
              setFormData((prev) =>
                produce(prev, (draft) => {
                  draft.planDesc = content;
                })
              );
            }}
            placeholderText="Write your planPartner description here..."
            editorKey={`planPartnerDesc-main-${id}`} // Ensure unique editor key
          />)}
        </div>
        
        {/* 3. Partner Plan - Date */}
        <div id="planPartnerStartDate" className="bg-gray-100 rounded p-1 m-1 w-full">
          <label
            htmlFor="planStartDate"
            className="block text-gray-600 text-sm font-bold mb-1"
          >
            Partner Plan Date
          </label>
          <input
            type="date"
            name="planStartDate"
            value={formData.planDate ? formData.planDate.split('T')[0] : ''}
            onChange={(e) =>
              setFormData((prev) =>
                produce(prev, (draft) => {
                  draft.planDate = e.target.value;
                })
              )
            }
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        {/* 4. Mission Selection */}
        <div id="missionId" className="bg-gray-100 rounded p-1 m-1">
          <label htmlFor="missionId" className="block text-gray-600 text-sm font-bold mb-1">
            Select Mission
          </label>
          <select
            name="missionId"
            value={formData.missionId || ""}
            onChange={handleChange}
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">-- Select Mission --</option>
            {/* {missions?.map((mission) => (
              <option key={mission._id} value={mission._id}>
                {mission.missionName}
              </option>
            ))} */}
            {/* 27 Aug 2025 */}
            {missions?.map((mission) => {
              const displayName = mission?.sharedMissionId
                ? mission?.sharedMissionId?.missionName
                : mission?.missionName;

              return (
                <option key={mission._id} value={mission._id}>
                  {displayName}
                </option>
              );
            })}
          </select>
        </div>

        {/* 5. Select Session for this Plan - 25 Aug 2025 */}
        <div id="sessionId" className="bg-gray-100 rounded p-1 m-1">
          <label htmlFor="sessionId" className="block text-gray-600 text-sm font-bold mb-1">
            Select Session (MissionPlan or MissionExecute)
          </label>
          <select
            name="sessionId"
            value={formData.sessionId || ""}
            onChange={handleChange}
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">-- Select Session --</option>
            {sessions
              ?.filter(
                (session) =>
                  session.sessionTypeId?.sessionTypeName === "MissionPlan" ||
                  session.sessionTypeId?.sessionTypeName === "MissionExecute"
              )
              .map((session) => {
                const sessionTypeName = session.sessionTypeId?.sessionTypeName || "Unknown";
                return (
                  <option key={session._id} value={session._id}>
                    {`${sessionTypeName} – ${session.sessionName}`}
                  </option>
                );
              })}
          </select>
        </div>

        {/* 6. Plan Goals Selection - 27 Aug 2025 */}
        <div id="planGoals" className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-start items-center mb-2">
            <p className="font-bold text-sm m-2">Plan Goals</p>  
            {filteredResolvedGoals.length === 0  && (
              <p className="font-bold text-sm m-2 p-2 bg-orange-100 text-orange-800 rounded-full">No goals have been added to this plan yet!</p> 
            )} 
            {filteredResolvedGoals.length>0 && availableGoalsForDropdown?.length === 0  && (
              <p className="font-bold text-sm m-2 p-2 bg-orange-100 text-orange-800 rounded-full">All available goals have been added to this plan!</p> 
            )} 
          </div>

          {/* Dropdown to Add New Goal */}
          {availableGoalsForDropdown?.length > 0 && (
            <div className="flex justify-between items-center m-2 mb-2">
              <select
                name="selectedGoalId"
                value={selectedGoalId || ""}
                onChange={(e) => setSelectedGoalId(e.target.value)}
                className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">-- Select a Goal --</option>
                {availableGoalsForDropdown.map((goal) => (
                  <option key={goal.missionGoalId} value={goal.missionGoalId}>
                    {goal.displayName}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
                onClick={() => {
                  if (selectedGoalId) {
                    const selectedGoalObj = filteredResolvedGoals.find(
                      (goal) => goal.missionGoalId === selectedGoalId
                    );
                    if (
                      selectedGoalObj &&
                      !formData.planGoals.some(
                        (goal) =>
                          goal.connectedMissionGoalId === selectedGoalId
                      )
                    ) {
                      setFormData(produce((draft) => {
                        draft.planGoals.push({
                          connectedMissionGoalId: selectedGoalObj.missionGoalId,
                          connectedSharedGoalId: selectedGoalObj.sharedMissionGoalId || null,
                          sharedMissionGoalId: selectedGoalObj.sharedMissionGoalId || null,
                          connectedGoalBreakdownId: null,
                        });
                      }));
                      setSelectedGoalId(""); // Reset
                    }
                  }
                }}
              >
                <PlusIcon className="h-4 w-4 inline" />
              </button>
            </div>
          )}

          {/* Display Linked Goals */}
          {formData.planGoals?.length > 0 && (
            <div className="mt-2 space-y-2">
              {formData.planGoals.map((goal, index) => {
                const displayGoal = filteredResolvedGoals.find(
                  (g) => g.missionGoalId === goal.connectedMissionGoalId
                );

                return (
                  <div
                    key={goal._id || `${goal.connectedMissionGoalId}-${index}`}
                    className="flex items-center justify-between bg-white p-2 rounded shadow"
                  >
                    <p className="text-sm text-gray-800">
                      {displayGoal?.displayName || "Unnamed Goal"}
                    </p>
                    <button
                      type="button"
                      className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold p-1 rounded"
                      onClick={() => {
                        // Mark for deletion if it exists in DB
                        if (goal._id) {
                          setPlanGoalsMarkedForDeletion(prev => [...prev, goal]);
                        }
                        // Remove from formData
                        setFormData(produce(draft => {
                          draft.planGoals.splice(index, 1);
                        }));
                      }}
                    >
                      <MinusIcon className="h-4 w-4 inline" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 7. Plan Actions */}
        <div id="planActions" className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Plan Actions</p>
            <button
              type="button"
              className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
              onClick={handleAddAction} // Use the existing function to add a new action
            >
              <PlusIcon className="h-4 w-4 inline" />
            </button>
          </div>

          {/* Render existing actions */}
          {formData.existingActions?.map((action, actionIndex) => (
            <div key={actionIndex} className="p-2 border rounded mb-2 bg-white">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="text-orange-600 text-xs font-bold m-1 p-1 rounded"
                    onClick={() => toggleSection(`existingAction-${actionIndex}`)} // Toggle collapse/expand
                  >
                    {collapsedSections[`existingAction-${actionIndex}`] ? (
                      <PlusIcon className="h-4 w-4" />
                    ) : (
                      <MinusIcon className="h-4 w-4" />
                    )}
                  </button>
                  <p className="font-bold text-xs">
                    Action {actionIndex + 1}: {action?.userActionName || "Unnamed"}
                  </p>
                </div>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                  onClick={() =>
                    setFormData((prevState) =>
                      produce(prevState, (draft) => {
                        // Mark the action as deleted for backend processing
                        draft.planActions.push({ planActionId: draft.existingActions[actionIndex].actionId, delete: true });
                        // Remove from existingActions for UI
                        draft.existingActions.splice(actionIndex, 1);
                      })
                    )
                  }
                  
                >
                  <MinusIcon className="h-4 w-4 inline" />
                </button>
              </div>

              {/* Collapsible content for editing the action */}
              {!collapsedSections[`existingAction-${actionIndex}`] && (
                <>
                  {/* Action Name */}
                  <div id={`actionName-${actionIndex}`} className="bg-gray-100 rounded p-1 m-1">
                    <label
                      htmlFor={`actionName-${actionIndex}`}
                      className="block text-gray-600 text-sm font-bold mb-1"
                    >
                      Action Name
                    </label>
                    <input
                      type="text"
                      name={`actionName-${actionIndex}`}
                      value={action?.userActionName || ""}
                      onChange={(e) =>
                        setFormData((prev) =>
                          produce(prev, (draft) => {
                            if (!draft.existingActions[actionIndex]) {
                              draft.existingActions[actionIndex] = {};
                            }
                            draft.existingActions[actionIndex].userActionName = e.target.value;
                          })
                        )
                      }
                      placeholder="Enter action name"
                      className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Action Description */}
                  <div id={`actionDesc-${actionIndex}`} className="bg-gray-100 rounded p-1 m-1">
                    <label
                      htmlFor={`actionDesc-${actionIndex}`}
                      className="block text-gray-600 text-sm font-bold mb-1"
                    >
                      Action Description
                    </label>
                    <EtContentEditorLight2
                      value={action?.userActionDesc || ""}
                      handleChange={(content) =>
                        setFormData((prev) =>
                          produce(prev, (draft) => {
                            if (!draft.existingActions[actionIndex]) {
                              draft.existingActions[actionIndex] = {};
                            }
                            draft.existingActions[actionIndex].userActionDesc = content;
                          })
                        )
                      }
                      placeholderText="Write your action description here..."
                      editorKey={`actionDesc-${actionIndex}-${action?.actionId}`} // Ensure unique editor key
                    />
                  </div>

                  {/* Complete By */}
                  <div id={`completeBy-${actionIndex}`} className="bg-gray-100 rounded p-1 m-1">
                    <label
                      htmlFor={`completeBy-${actionIndex}`}
                      className="block text-gray-600 text-sm font-bold mb-1"
                    >
                      Complete By
                    </label>
                    <input
                      type="date"
                      name={`completeBy-${actionIndex}`}
                      value={
                        action?.completeBy
                          ? action?.completeBy.split('T')[0]
                          : ""
                      }
                      onChange={(e) =>
                        setFormData((prev) =>
                          produce(prev, (draft) => {
                            if (!draft.existingActions[actionIndex]) {
                              draft.existingActions[actionIndex] = {};
                            }
                            draft.existingActions[actionIndex].completeBy = e.target.value;
                          })
                        )
                      }
                      className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                </>
              )}
            </div>
          ))}
        </div>

        {/* Render new actions */}
        {formData.newActions?.map((action, actionIndex) => (
          <div key={`new-${actionIndex}`} className="p-2 border rounded mb-2 bg-white">
            <div className="flex justify-between items-center mb-2">
              <p className="font-bold text-xs">
                New Action {actionIndex + 1}: {action?.userActionName || "Unnamed"}
              </p>
              <button
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                onClick={() =>
                  setFormData((prevState) =>
                    produce(prevState, (draft) => {
                      draft.newActions.splice(actionIndex, 1); // Remove new action
                    })
                  )
                }
              >
                <MinusIcon className="h-4 w-4 inline" />
              </button>
            </div>

            {/* Editable fields for new actions */}
            <div id={`newActionName-${actionIndex}`} className="bg-gray-100 rounded p-1 m-1">
              <label
                htmlFor={`newActionName-${actionIndex}`}
                className="block text-gray-600 text-sm font-bold mb-1"
              >
                Action Name
              </label>
              <input
                type="text"
                name={`newActionName-${actionIndex}`}
                value={action?.userActionName || ""}
                onChange={(e) =>
                  setFormData((prevState) =>
                    produce(prevState, (draft) => {
                      draft.newActions[actionIndex].userActionName = e.target.value;
                    })
                  )
                }
                placeholder="Enter action name"
                className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Additional fields like Action Description and Complete By */}
            {/* Action Description */}
            <div id={`newActionDesc-${actionIndex}`} className="bg-gray-100 rounded p-1 m-1">
              <label
                htmlFor={`newActionDesc-${actionIndex}`}
                className="block text-gray-600 text-sm font-bold mb-1"
              >
                Action Description
              </label>
              <EtContentEditorLight2
                value={action?.userActionDesc || ""}
                handleChange={(content) =>
                  setFormData((prevState) =>
                    produce(prevState, (draft) => {
                      draft.newActions[actionIndex].userActionDesc = content;
                    })
                  )
                }
                placeholderText="Write your action description here..."
                editorKey={`newActionDesc-${actionIndex}`} // Ensure unique editor key
              />
            </div>

            <div id={`newActionCompleteBy-${actionIndex}`} className="bg-gray-100 rounded p-1 m-1">
              <label
                htmlFor={`newActionCompleteBy-${actionIndex}`}
                className="block text-gray-600 text-sm font-bold mb-1"
              >
                Complete By
              </label>
              <input
                type="date"
                name={`newActionCompleteBy-${actionIndex}`}
                value={action?.completeBy || ""}
                onChange={(e) =>
                  setFormData((prevState) =>
                    produce(prevState, (draft) => {
                      draft.newActions[actionIndex].completeBy = e.target.value;
                    })
                  )
                }
                className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>
        ))}

        {/* 8. Partner Plan - Notes */}
        <div id="planPartnerNotes" className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Partner Plan Notes</p>
            <button
              type="button"
              className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
              onClick={() =>
                setFormData((prev) =>
                  produce(prev, (draft) => {
                    draft.planNotes.push({
                      planNoteText: "",
                      planNoteDate: new Date().toISOString(),
                    });
                  })
                )
              }
            >
              <PlusIcon className="h-4 w-4 inline" />
            </button>
          </div>

          {formData.planNotes?.map((note, index) => (
            <div key={index} className="p-2 border rounded mb-2 bg-white">
              <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-xs">Note {index + 1}</p>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold m-1 p-1 rounded"
                  onClick={() =>
                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.planNotes[index].delete = true; // Mark as deleted
                      })
                    )
                  }
                  
                >
                  <MinusIcon className="h-4 w-4 inline" />
                </button>
              </div>

              {/* Note Text */}
              <div className="bg-gray-100 rounded p-1 m-1">
                <label
                  htmlFor={`planPartnerNoteText-${index}`}
                  className="block text-gray-600 text-sm font-bold mb-1"
                >
                  Note Text
                </label>
                {isFormInitialized && ( 
                <EtContentEditorLight2
                  value={note.planNoteText}
                  handleChange={(content) => {
                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.planNotes[index].planNoteText = content;
                      })
                    );
                  }}
                  placeholderText="Write your note here..."
                  editorKey={`planPartnerNote-${index}-${note?._id || `new-${index}`}`} // Ensure unique editor key
                />)}
              </div>
            </div>
          ))}
        </div>

        {/* 9. Form Submissions Bar */}
        <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
            <EtBtnFormBasic 
              name={'Update'} 
              type={'create'} 
              handleClick={handleSubmit} 
              />
            <EtBtnFormBasic
              name={'Clear'}
              type={'clear'}
              handleClick={handleClear}
            />
            <EtBtnFormBasic
              name={'Cancel'}
              type={'cancel'}
              handleClick={handleCancel}
            />
        </div>

      </form>
      
    </div>
  )
}

export default EtUpdateUserImprintPlanPartnerComp